import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import useIsMobileSize from '@/hooks/useIsMobileSize'

const BinaryButtons = ({
  firstButton = {},
  secondButton = {},
  selectedId = null,
  className = '',
  gap = 'gap-2',
  size = 'medium',
  renderPosition = null /* can be bottom-sticky */,
  hidden = false,
}) => {
  /* The buttons are a fixed height and `justify-center` centres their row, so they carry
     horizontal padding only: vertical padding would fight that the moment a row is taller
     than the padding box — which is what an odds row, at 16px, already is. */
  const hasValues = Boolean(firstButton?.value || secondButton?.value)
  const sizeClasses = size === 'large' ? 'h-16 px-5' : 'h-12 px-4'
  const labelSizeClass = size === 'large' ? 'text-base' : 'text-sm'
  // Odds rows set their own weights: the position label stays regular, the percentage carries it.
  const rowTypeClass = hasValues ? 'text-base leading-none' : `${labelSizeClass} font-bold`
  const [isFirstHovered, setIsFirstHovered] = useState(false)
  const [isSecondHovered, setIsSecondHovered] = useState(false)
  const isMobileSize = useIsMobileSize()

  const {
    label: firstLabel = 'Yes',
    icon: firstIcon = null,
    hoverContent: firstHoverContent = null,
    value: firstValue = null,
    onClick: firstOnClick = () => {},
    outcomeId: firstOutcomeId = null,
    className: firstClassName = '',
    disabled: firstDisabled = false,
    featured: firstFeatured = false,
    dimmed: firstDimmed = false,
  } = firstButton

  const {
    label: secondLabel = 'No',
    icon: secondIcon = null,
    hoverContent: secondHoverContent = null,
    value: secondValue = null,
    onClick: secondOnClick = () => {},
    outcomeId: secondOutcomeId = null,
    className: secondClassName = '',
    disabled: secondDisabled = false,
    featured: secondFeatured = false,
    dimmed: secondDimmed = false,
  } = secondButton

  const isSticky = renderPosition === 'bottom-sticky'
  const firstSelected = !firstDisabled && selectedId === firstOutcomeId
  const secondSelected = !secondDisabled && selectedId === secondOutcomeId

  const content = (
    <div
      className={classNames('flex my-auto w-full', gap, className, {
        // Sticky mode only exists on the market page, which has no tab bar (App.jsx
        // shouldDisplayTabbar), so the bar sits on the viewport edge.
        'fixed left-0 right-0 bottom-0 !z-[1000] px-4 pt-4 pb-[max(16px,env(safe-area-inset-bottom,16px))] bg-surface-2 transition-all duration-300':
          isSticky,
        'translate-y-full opacity-0 invisible pointer-events-none': isSticky && hidden,
      })}
      aria-hidden={(isSticky && hidden) || undefined}>
      {/* First Button (Yes/Up) */}
      <div
        className={classNames(
          'flex flex-col justify-center rounded-[var(--radius-sm)] transition-all duration-200 w-full',
          sizeClasses,
          firstDisabled ? 'cursor-default' : 'cursor-pointer',
          !firstClassName && !firstFeatured && !firstDimmed && 'bg-positive-4/30 text-Neutral-6 hover:bg-positive-4/50',
          {
            'font-bold transform': firstSelected || firstFeatured,
            '!bg-positive-4/100 !text-white': (firstSelected || firstFeatured) && !firstClassName,
            '!bg-Neutral-3/50 !text-Neutral-4 opacity-50 saturate-50': firstDimmed && !firstClassName,
          },
          firstClassName
        )}
        aria-disabled={firstDisabled || undefined}
        onMouseEnter={() => !isMobileSize && !firstDisabled && setIsFirstHovered(true)}
        onMouseLeave={() => !isMobileSize && setIsFirstHovered(false)}
        onClick={firstDisabled ? undefined : firstOnClick}>
        <div className={classNames('flex w-full items-center', rowTypeClass)}>
          {isFirstHovered && firstHoverContent && !isMobileSize && !firstDisabled ? (
            /* Same centred row as the label it replaces, so the odds sit on the button's axis. */
            <span className="w-full text-center tabular-nums">{firstHoverContent}</span>
          ) : (
            <div
              className={classNames('flex items-center gap-2', firstValue ? 'w-full justify-between' : 'w-full justify-center')}>
              {firstIcon && firstIcon}
              <span className={firstValue ? 'font-normal' : undefined}>{firstLabel}</span>
              {firstValue && <span className="font-bold tabular-nums">{firstValue}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Second Button (No/Down) */}
      <div
        className={classNames(
          'flex flex-col justify-center rounded-[var(--radius-sm)] transition-all duration-200 w-full',
          sizeClasses,
          secondDisabled ? 'cursor-default' : 'cursor-pointer',
          !secondClassName && !secondFeatured && !secondDimmed && 'bg-auxiliaries-3/30 text-Neutral-6 hover:bg-auxiliaries-3/50',
          {
            'font-bold transform': secondSelected || secondFeatured,
            '!bg-auxiliaries-3/100 !text-white': (secondSelected || secondFeatured) && !secondClassName,
            '!bg-Neutral-3/50 !text-Neutral-4 opacity-50 saturate-50': secondDimmed && !secondClassName,
          },
          secondClassName
        )}
        aria-disabled={secondDisabled || undefined}
        onMouseEnter={() => !isMobileSize && !secondDisabled && setIsSecondHovered(true)}
        onMouseLeave={() => !isMobileSize && setIsSecondHovered(false)}
        onClick={secondDisabled ? undefined : secondOnClick}>
        <div className={classNames('flex w-full items-center', rowTypeClass)}>
          {isSecondHovered && secondHoverContent && !isMobileSize && !secondDisabled ? (
            /* Same centred row as the label it replaces, so the odds sit on the button's axis. */
            <span className="w-full text-center tabular-nums">{secondHoverContent}</span>
          ) : (
            <div
              className={classNames('flex items-center gap-2', secondValue ? 'w-full justify-between' : 'w-full justify-center')}>
              {secondIcon && secondIcon}
              <span className={secondValue ? 'font-normal' : undefined}>{secondLabel}</span>
              {secondValue && <span className="font-bold tabular-nums">{secondValue}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  if (renderPosition === 'bottom-sticky' && typeof document !== 'undefined') {
    return createPortal(content, document.body)
  }

  return content
}

export default BinaryButtons
